import { useQuery } from "@tanstack/react-query";
import {
  CATEGORIES_DEFAULT_BG_COLOR,
  CATEGORIES_DEFAULT_ICON,
  CATEGORIES_DEFAULT_TEXT_COLOR,
  ETransactionType,
  SOURCES_DEFAULT_BG_COLOR,
  SOURCES_DEFAULT_ICON,
  SOURCES_DEFAULT_TEXT_COLOR,
} from "../helpers/constants";
import { searchTransactions } from "../api/transactions";
import { useCallback, useMemo } from "react";
import { ALL_TRANSACTION_STATUSES } from "gastos-shared";
import { compact, filter, find, map, merge, sortBy, uniq } from "lodash-es";
import { getCategories } from "src/api/categories";
import { getSources } from "src/api/sources";
import { filterTransactions, rgbaObjectToString } from "src/helpers/utils";
import useExtraModules from "./useExtraModules";
import useMainFilter from "./useMainFilter";
import { CACHE_KEYS } from "src/constants/cache";
import { ECustomSelectIconType } from "src/components/Select/CustomSelect";

const REFETCH_MINUTES = 10;
const REFETCH_INTERVAL = REFETCH_MINUTES * 1000 * 60;

export default function useAllTransactions() {
  const { mainFilter } = useMainFilter();
  const { myNetworkIsEnabled } = useExtraModules();

  const {
    data: customizedCategories,
    isLoading: isLoadingCustomizedCategories,
    isRefetching: isRefetchingCustomizedCategories,
  } = useQuery({
    queryFn: async () => await getCategories(),
    queryKey: [CACHE_KEYS.ALL_CATEGORIES],
    enabled: true,
  });

  const {
    data: customizedSources,
    isLoading: isLoadingCustomizedSources,
    isRefetching: isRefetchingCustomizedSources,
  } = useQuery({
    queryFn: async () => await getSources(),
    queryKey: [CACHE_KEYS.ALL_SOURCES],
    enabled: true,
  });

  const {
    data: allTransactions,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [CACHE_KEYS.ALL_TRANSACTIONS],
    queryFn: () => searchTransactions({ fieldsToSearch: {} }),
    refetchInterval: REFETCH_INTERVAL,
    enabled: true, //!!mainFilter.account,
  });

  const allPendingTransactions = useMemo(() => {
    return filterTransactions(allTransactions?.data || [], {
      shared: false,
      archived: false,
      deleted: false,
      status: ALL_TRANSACTION_STATUSES.PENDING,
    });
  }, [allTransactions?.data]);

  // Filtrado de las transacciones activas y no eliminadas
  const allActiveTransactions = useMemo(() => {
    // if (!mainFilter?.account) return [];
    return (
      allTransactions?.data?.filter(
        ({
          archived,
          shared,
          deleted,
        }: {
          archived: boolean;
          shared: boolean;
          deleted: boolean;
        }) => !archived && !shared && !deleted,
      ) || []
    );
  }, [allTransactions?.data, mainFilter?.account, isLoading, isRefetching]);

  // Función para obtener una transacción por su ID
  const getTransactionById = ({ transactionId }: { transactionId: string }) =>
    find(allTransactions?.data || [], { invoice: transactionId });

  // Filtrado de transacciones pendientes de pagar
  const allTransactionsPendingToPay = useMemo(() => {
    // if (!mainFilter?.account) return [];

    return allPendingTransactions.filter(
      (transaction: any) =>
        transaction?.status === ALL_TRANSACTION_STATUSES.PENDING &&
        transaction.type === ETransactionType.EXPENSE,
    );
  }, [allPendingTransactions]);

  // Filtrado de transacciones pendientes de cobrar
  const allTransactionsPendingToCollect = useMemo(() => {
    // if (!mainFilter?.account) return [];

    return allPendingTransactions.filter(
      (transaction: any) =>
        transaction?.status === ALL_TRANSACTION_STATUSES.PENDING &&
        transaction.type === ETransactionType.INCOME,
    );
  }, [allPendingTransactions]);

  // Filtrado de transacciones recibidas
  const allReceivedTransactions = useMemo(() => {
    if (!myNetworkIsEnabled) return [];

    return (
      allTransactions?.data?.filter(
        ({ shared, deleted }: { shared: boolean; deleted: boolean }) => shared,
      ) || []
    );
  }, [allTransactions?.data, myNetworkIsEnabled]);

  const allClientProviders = useMemo(
    () => uniq(compact(map(allTransactions?.data, "payTo"))),
    [allTransactions?.data, mainFilter?.account],
  );

  const allClientProvidersObject = useMemo(
    () =>
      sortBy(
        map(allClientProviders, (clientProvider) => ({
          value: clientProvider,
          label: clientProvider,
        })),
        "label",
      ),
    [allClientProviders],
  );

  const getCategoryCustomSettings = useCallback(
    (category: string) => {
      const res = find(customizedCategories?.data, { category });

      return merge(
        {},
        {
          bg: CATEGORIES_DEFAULT_BG_COLOR,
          color: CATEGORIES_DEFAULT_TEXT_COLOR,
          icon: CATEGORIES_DEFAULT_ICON,
        },
        {
          ...res?.settings,
        },
      );
    },
    [isLoadingCustomizedCategories, customizedCategories],
  );

  const allCategories = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "category"))),
    [allTransactions?.data],
  );

  const allCategoriesObject = useMemo(
    () =>
      sortBy(
        map(allCategories, (category) => ({
          value: category,
          label: category,
          icon: getCategoryCustomSettings(category)?.icon,
          iconStyles: {
            background: rgbaObjectToString(
              getCategoryCustomSettings(category)?.bg,
            ),
            color: rgbaObjectToString(
              getCategoryCustomSettings(category)?.color,
            ),
          },
          iconType: ECustomSelectIconType.ICON,
        })),
        "label",
      ),
    [allCategories],
  );

  const categoryExists = (category: string) => {
    return allCategories?.includes(category);
  };

  const allSources = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "payTo"))),
    [allTransactions?.data],
  );

  const allDescriptions = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "description"))),
    [allTransactions?.data],
  );

  const allDescriptionsObject = useMemo(
    () =>
      sortBy(
        map(allDescriptions, (description) => ({
          value: description,
          label: description,
        })),
        "label",
      ),
    [allDescriptions],
  );

  const getSourceCustomSettings = useCallback(
    (source: string) => {
      const res = find(customizedSources?.data, { source });

      return merge(
        {},
        {
          bg: SOURCES_DEFAULT_BG_COLOR,
          color: SOURCES_DEFAULT_TEXT_COLOR,
          icon: SOURCES_DEFAULT_ICON,
          notes: [],
          contact: [],
          picture: null,
        },
        {
          ...res?.settings,
        },
      );
    },
    [
      isLoadingCustomizedSources,
      customizedCategories,
      isRefetchingCustomizedSources,
    ],
  );

  const allSourcesObject = useMemo(
    () =>
      sortBy(
        map(allSources, (source) => ({
          value: source,
          label: source,
          description: getSourceCustomSettings(source)?.address || "",
          icon: getSourceCustomSettings(source)?.icon,
          iconStyles: {
            background: rgbaObjectToString(getSourceCustomSettings(source)?.bg),
            color: rgbaObjectToString(getSourceCustomSettings(source)?.color),
          },
          iconType: ECustomSelectIconType.ICON,
        })),
        "label",
      ),
    [
      allSources,
      customizedSources,
      isLoadingCustomizedSources,
      isRefetchingCustomizedSources,
    ],
  );

  const sourceExists = (source: string) => {
    return allSources?.includes(source);
  };

  const getAllTransactionsByAccount = useCallback(
    (account: string) => {
      return filter(allTransactions?.data || [], {
        account,
      });
    },
    [allTransactions],
  );

  return {
    allTransactions: allTransactions?.data,
    allActiveTransactions,
    isLoadingAllTransactions: !!isLoading,
    allTransactionsPendingToCollect,
    allTransactionsPendingToPay,
    allReceivedTransactions,
    getTransactionById,
    isRefetchingAllTransactions: !!isRefetching,
    allClientProviders,
    allClientProvidersObject,
    allCategories,
    allCategoriesObject,
    isLoadingCustomizedCategories,
    isRefetchingCustomizedCategories,
    customizedCategories: customizedCategories?.data,
    getCategoryCustomSettings,
    categoryExists,
    allSources,
    allSourcesObject,
    customizedSources: customizedSources?.data,
    isLoadingCustomizedSources,
    isRefetchingCustomizedSources,
    getSourceCustomSettings,
    sourceExists,
    allDescriptions,
    allDescriptionsObject,
    allPendingTransactions,
    getAllTransactionsByAccount,
  };
}
